import { revenueFields, expenseFields } from "./Constants";
import { toSnakeCase } from "./Formatting";
import { getDefaultStore } from "jotai";
import { atomFamily, atomWithStorage, RESET } from "jotai/utils";

type DriverLine = {
  qty?: number;
  unit?: string;
  rate?: number | null;
  impact?: number;
};

type DriverEntryStore = {
  comments?: string;
  categories: Record<string, DriverLine>; // categorySnake -> DriverLine
};

type DriversStore = Record<string, DriverEntryStore>; // driverSnake -> entry

const safeParse = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const cloneStore = (store: DriversStore): DriversStore => safeParse<DriversStore>(JSON.stringify(store), {});

const hasMeaningfulLineData = (line?: DriverLine) =>
  (typeof line?.qty === "number" && Math.abs(line.qty) > 0) ||
  (typeof line?.impact === "number" && Math.abs(line.impact) > 0);

const hasMeaningfulDriverData = (entry?: DriverEntryStore) =>
  !!entry &&
  ((entry.comments?.trim().length ?? 0) > 0 || Object.values(entry.categories ?? {}).some((line) => hasMeaningfulLineData(line)));

export const hasMeaningfulDriversStore = (store: DriversStore): boolean =>
  Object.values(store).some((entry) => hasMeaningfulDriverData(entry));

export const getVoyageDriversStoreKey = (tcCode?: string | null, voyageNo?: string | number | null) => {
  const tc = tcCode ? String(tcCode) : "";
  const v = voyageNo !== undefined && voyageNo !== null ? String(voyageNo) : "";
  return `Drivers_${tc}_${v}`; // e.g., "Drivers_BS56-I0003_29"
};

export const hasVoyageDriversStoreSnapshot = (
  tcCode?: string | null,
  voyageNo?: string | number | null,
): boolean => {
  return hasMeaningfulDriversStore(loadDriversStore(tcCode, voyageNo));
};

const jotaiStore = getDefaultStore();

const driversStoreAtomFamily = atomFamily((storageKey: string) =>
  atomWithStorage<DriversStore>(storageKey, {}, undefined, { getOnInit: true }),
);

const getDriversStoreAtom = (tcCode?: string | null, voyageNo?: string | number | null) =>
  driversStoreAtomFamily(getVoyageDriversStoreKey(tcCode, voyageNo));

export const loadDriversStore = (tcCode?: string | null, voyageNo?: string | number | null): DriversStore => {
  const current = jotaiStore.get(getDriversStoreAtom(tcCode, voyageNo));
  return cloneStore(current ?? {});
};

export const saveDriversStore = (tcCode: string | undefined, voyageNo: string | undefined, store: DriversStore) => {
  jotaiStore.set(getDriversStoreAtom(tcCode, voyageNo), cloneStore(store));
};

const ensureDriverCategory = (store: DriversStore, driverSnake: string, categorySnake: string) => {
  if (!store[driverSnake]) store[driverSnake] = { categories: {} };
  if (!store[driverSnake].categories[categorySnake]) store[driverSnake].categories[categorySnake] = {};
};

export const setDriverLine = (
  tcCode: string | undefined,
  voyageNo: string | undefined,
  driverName: string, // display
  categoryName: string, // display
  partial: Partial<DriverLine>,
) => {
  const store = loadDriversStore(tcCode, voyageNo);
  const driverSnake = toSnakeCase(driverName);
  const categorySnake = toSnakeCase(categoryName);
  ensureDriverCategory(store, driverSnake, categorySnake);
  Object.assign(store[driverSnake].categories[categorySnake], partial);
  saveDriversStore(tcCode, voyageNo, store);
};

export const getDriverLine = (
  tcCode: string | undefined,
  voyageNo: string | undefined,
  driverName: string,
  categoryName: string,
): DriverLine | undefined => {
  const store = loadDriversStore(tcCode, voyageNo);
  const driverSnake = toSnakeCase(driverName);
  const categorySnake = toSnakeCase(categoryName);
  return store[driverSnake]?.categories?.[categorySnake];
};

export const setDriverComment = (
  tcCode: string | undefined,
  voyageNo: string | undefined,
  driverName: string,
  comment: string,
) => {
  const store = loadDriversStore(tcCode, voyageNo);
  const driverSnake = toSnakeCase(driverName);
  if (!store[driverSnake]) store[driverSnake] = { categories: {} };
  store[driverSnake].comments = comment;
  saveDriversStore(tcCode, voyageNo, store);
};

export const getDriverComment = (
  tcCode: string | undefined,
  voyageNo: string | undefined,
  driverName: string,
): string => {
  const store = loadDriversStore(tcCode, voyageNo);
  const driverSnake = toSnakeCase(driverName);
  return store[driverSnake]?.comments ?? "";
};

export const removeDriverFromStore = (tcCode: string | undefined, voyageNo: string | undefined, driverName: string) => {
  const store = loadDriversStore(tcCode, voyageNo);
  const driverSnake = toSnakeCase(driverName);
  if (store[driverSnake]) {
    delete store[driverSnake];
    saveDriversStore(tcCode, voyageNo, store);
  }
};

export const clearVoyageDriversStore = (tcCode: string | undefined, voyageNo: string | undefined) => {
  jotaiStore.set(getDriversStoreAtom(tcCode, voyageNo), RESET);
};

// Used drivers from the new store
export const getDriversWithDataFromStore = (tcCode: string | undefined, voyageNo: string | undefined): string[] => {
  const store = loadDriversStore(tcCode, voyageNo);
  const names: string[] = [];
  for (const [driverSnake, entry] of Object.entries(store)) {
    if (hasMeaningfulDriverData(entry)) {
      names.push(driverSnake.replace(/_/g, " "));
    }
  }
  return names;
};

export const getDriverImpactSumFromStore = (
  tcCode: string | undefined,
  voyageNo: string | undefined,
  driverName: string,
): number => {
  const store = loadDriversStore(tcCode, voyageNo);
  const driverSnake = toSnakeCase(driverName);
  const entry = store[driverSnake];
  if (!entry) return 0;
  const revenueSet = new Set(revenueFields.map(toSnakeCase));
  const expenseSet = new Set(expenseFields.map(toSnakeCase));

  let revenue = 0;
  let expense = 0;
  for (const [categorySnake, line] of Object.entries(entry.categories ?? {})) {
    const impact = typeof line.impact === "number" ? line.impact : 0;
    if (revenueSet.has(categorySnake)) revenue += impact;
    else if (expenseSet.has(categorySnake)) expense += impact;
  }
  return revenue - expense;
};
